import { useEffect, useRef, useState } from 'react'
import ContentCut from '@mui/icons-material/ContentCut'
import {
  Badge,
  Box,
  ButtonBase,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { Document } from '@/types/documents'
import FullscreenImageDialog from './FullscreenImageDialog'
import ImageCard from './ImageCard'
import useSplitDocuments from './useSplitDocuments'

type SplitDocumentPagesProps = {
  document: Document
  splitDocumentsData: ReturnType<typeof useSplitDocuments>
  isSplitView?: boolean
  tileWidth?: number
  useFullImage?: boolean
}

export const DEFAULT_TILE_WIDTH = 240
const SELECTED_BUTTON_WIDTH = 40
const UNSELECTED_BUTTON_WIDTH = 24

function getGroupLeft(
  splitPoint: number,
  numSelectedPointsBefore: number,
  tileWidth: number,
) {
  return (
    splitPoint * tileWidth +
    numSelectedPointsBefore * SELECTED_BUTTON_WIDTH +
    (splitPoint - numSelectedPointsBefore) * UNSELECTED_BUTTON_WIDTH
  )
}

function getGroupWidth(distanceToNextSplitPoint: number, tileWidth: number) {
  return (
    distanceToNextSplitPoint * tileWidth +
    (distanceToNextSplitPoint - 1) * UNSELECTED_BUTTON_WIDTH
  )
}

const GROUP_COLORS = [
  { dark: 'rgba(255, 167, 38, 0.25)', light: 'rgba(255, 167, 38, 0.2)' },
  { dark: 'rgba(66, 165, 245, 0.25)', light: 'rgba(66, 165, 245, 0.2)' },
  { dark: 'rgba(102, 187, 106, 0.25)', light: 'rgba(102, 187, 106, 0.2)' },
  { dark: 'rgba(171, 71, 188, 0.25)', light: 'rgba(171, 71, 188, 0.2)' },
  { dark: 'rgba(239, 83, 80, 0.25)', light: 'rgba(239, 83, 80, 0.2)' },
  { dark: 'rgba(38, 198, 218, 0.25)', light: 'rgba(38, 198, 218, 0.2)' },
]

const GROUP_PADDING = 12
const GROUP_LABEL_HEIGHT = 20

type GroupBackgroundProps = {
  splitPoint: number
  distanceToNextSplitPoint: number
  numSelectedPointsBefore: number
  groupIndex: number
  tileWidth?: number
}

function GroupBackground({
  splitPoint,
  distanceToNextSplitPoint,
  numSelectedPointsBefore,
  groupIndex,
  tileWidth = DEFAULT_TILE_WIDTH,
}: GroupBackgroundProps) {
  const theme = useTheme()
  const colors = GROUP_COLORS[groupIndex % GROUP_COLORS.length]
  return (
    <Box
      sx={{
        position: 'absolute',
        top: GROUP_LABEL_HEIGHT,
        bottom: 0,
        left:
          getGroupLeft(splitPoint, numSelectedPointsBefore, tileWidth) -
          GROUP_PADDING,
        width:
          getGroupWidth(distanceToNextSplitPoint, tileWidth) +
          GROUP_PADDING * 2,
        backgroundColor:
          theme.palette.mode === 'dark' ? colors.dark : colors.light,
        borderRadius: 2,
        pointerEvents: 'none',
        transition: 'all 0.15s ease-in-out',
      }}
    />
  )
}

type GroupLabelProps = {
  splitPoint: number
  distanceToNextSplitPoint: number
  numSelectedPointsBefore: number
  groupIndex: number
  tileWidth?: number
}

function GroupLabel({
  splitPoint,
  distanceToNextSplitPoint,
  numSelectedPointsBefore,
  groupIndex,
  tileWidth = DEFAULT_TILE_WIDTH,
}: GroupLabelProps) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        height: GROUP_LABEL_HEIGHT,
        left:
          getGroupLeft(splitPoint, numSelectedPointsBefore, tileWidth) -
          GROUP_PADDING,
        width:
          getGroupWidth(distanceToNextSplitPoint, tileWidth) +
          GROUP_PADDING * 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          fontSize: '0.7rem',
          lineHeight: 1,
        }}
      >
        Doc {groupIndex + 1}
      </Typography>
    </Box>
  )
}

export default function SplitDocumentPages({
  document,
  splitDocumentsData,
  isSplitView = false,
  tileWidth = DEFAULT_TILE_WIDTH,
  useFullImage,
}: SplitDocumentPagesProps) {
  const theme = useTheme()
  const fullImageOverlay = useOverlay()
  const imagesContainerRef = useRef<HTMLDivElement>(null)
  const indicatorsRef = useRef<HTMLDivElement>(null)
  const { pagesToDelete, splitPoints, setPagesToDelete, setSplitPoints } =
    splitDocumentsData
  const [fullImagePageIndex, setFullImagePageIndex] = useState<number | null>(
    null,
  )

  const pages = document.document_pages

  function onCloseFullImageDialog() {
    fullImageOverlay.close()
    if (fullImagePageIndex !== null) {
      if (isSplitView) {
        const childElement =
          imagesContainerRef.current?.children[fullImagePageIndex]
        childElement?.scrollIntoView({ inline: 'center' })
      }
      setFullImagePageIndex(null)
    }
  }

  useEffect(() => {
    if (imagesContainerRef.current) {
      imagesContainerRef.current.scrollLeft = 0
    }
    setFullImagePageIndex(null)
  }, [document.id])

  return (
    <Box
      sx={{
        overflowX: 'auto',
        overflowY: 'visible',
        position: 'relative',
        px: 10,
        pb: 3,
      }}
    >
      <Stack
        direction="row"
        sx={{
          pb: `${GROUP_PADDING}px`,
          pt: `${GROUP_LABEL_HEIGHT + GROUP_PADDING}px`,
          position: 'relative',
          mx: 'auto',
          width: 'fit-content',
        }}
        ref={imagesContainerRef}
        onScroll={() => {
          if (indicatorsRef.current && imagesContainerRef.current) {
            indicatorsRef.current.scrollLeft =
              imagesContainerRef.current.scrollLeft
          }
        }}
      >
        {/* Group backgrounds rendered behind images */}
        {splitPoints.length > 0 && (
          <Box
            ref={indicatorsRef}
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            <GroupBackground
              splitPoint={0}
              distanceToNextSplitPoint={splitPoints[0]}
              numSelectedPointsBefore={0}
              groupIndex={0}
              tileWidth={tileWidth}
            />
            {splitPoints.map((sp, i) => (
              <GroupBackground
                key={sp}
                splitPoint={sp}
                distanceToNextSplitPoint={
                  (splitPoints[i + 1] || pages.length) - sp
                }
                numSelectedPointsBefore={i + 1}
                groupIndex={i + 1}
                tileWidth={tileWidth}
              />
            ))}
            <GroupLabel
              splitPoint={0}
              distanceToNextSplitPoint={splitPoints[0]}
              numSelectedPointsBefore={0}
              groupIndex={0}
              tileWidth={tileWidth}
            />
            {splitPoints.map((sp, i) => (
              <GroupLabel
                key={sp}
                splitPoint={sp}
                distanceToNextSplitPoint={
                  (splitPoints[i + 1] || pages.length) - sp
                }
                numSelectedPointsBefore={i + 1}
                groupIndex={i + 1}
                tileWidth={tileWidth}
              />
            ))}
          </Box>
        )}
        {pages.map((page, index) => {
          const splitPoint = index + 1
          const splitPointIndex = splitPoints.findIndex(
            (point) => point === splitPoint,
          )

          const pointIsSelected = splitPointIndex !== -1

          // Get width based on larger page dimension
          return (
            <Stack key={page.id} direction="row">
              <Badge
                key={page.id}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                badgeContent={page.page_number}
                color="primary"
                component="div"
                max={10000} // Just in case we ever have a document that has more than 99 (default) pages
                slotProps={{
                  badge: () => ({
                    sx: { transform: 'scale(1) translate(-20%, -20%)' }, // Override default badge transform position
                  }),
                }}
              >
                <ImageCard
                  page={page}
                  pagesToDelete={pagesToDelete}
                  setPagesToDelete={setPagesToDelete}
                  tileWidth={tileWidth}
                  useFullImage={useFullImage}
                  onShowFullScreenImage={() => {
                    setFullImagePageIndex(index)
                    fullImageOverlay.open()
                  }}
                />
              </Badge>
              {index < pages.length - 1 && (
                <ButtonBase
                  sx={{
                    width: pointIsSelected
                      ? SELECTED_BUTTON_WIDTH
                      : UNSELECTED_BUTTON_WIDTH,
                    transition: 'all 0.15s ease-in-out',
                    cursor: 'pointer',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      height: 'calc(50% - 14px)',
                      width: pointIsSelected ? 3 : 2,
                      borderRadius: 1,
                      backgroundColor: pointIsSelected
                        ? theme.palette.secondary.main
                        : 'transparent',
                      transition: 'all 0.15s ease-in-out',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      height: 'calc(50% - 14px)',
                      width: pointIsSelected ? 3 : 2,
                      borderRadius: 1,
                      backgroundColor: pointIsSelected
                        ? theme.palette.secondary.main
                        : 'transparent',
                      transition: 'all 0.15s ease-in-out',
                    },
                    '&:hover': {
                      backgroundColor: (t) =>
                        t.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.06)',
                      '&::before, &::after': {
                        backgroundColor: pointIsSelected
                          ? theme.palette.secondary.main
                          : theme.palette.action.disabled,
                      },
                    },
                    '& .split-hint': {
                      opacity: pointIsSelected ? 1 : 0,
                      transition: 'opacity 0.15s ease-in-out',
                    },
                    '&:hover .split-hint': {
                      opacity: 1,
                    },
                  }}
                  onClick={() => {
                    if (!pointIsSelected) {
                      setSplitPoints(
                        [...splitPoints, splitPoint].toSorted((a, b) => a - b),
                      )
                    } else {
                      setSplitPoints(
                        splitPoints.filter((p) => p !== splitPoint),
                      )
                    }
                  }}
                >
                  <ContentCut
                    className="split-hint"
                    sx={{
                      fontSize: 16,
                      color: pointIsSelected
                        ? theme.palette.secondary.main
                        : theme.palette.text.disabled,
                    }}
                  />
                </ButtonBase>
              )}
            </Stack>
          )
        })}
      </Stack>
      <FullscreenImageDialog
        key={document.id}
        imagePageIndex={fullImagePageIndex}
        overlay={fullImageOverlay}
        pages={pages}
        onClose={onCloseFullImageDialog}
        setImagePageIndex={setFullImagePageIndex}
        showArrows={isSplitView}
      />
    </Box>
  )
}
